import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { MspZone, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const MSP_ZONE_CATEGORIES = [
  'MARINE_PROTECTED_AREA',
  'AQUACULTURE_PRIORITY_ZONE',
  'RENEWABLE_ENERGY_ZONE',
  'SHIPPING_LANE',
  'ARTISANAL_FISHING_PRIORITY_ZONE',
  'TOURISM_RECREATION_ZONE',
  'PORT_INDUSTRIAL_ZONE',
  'GENERAL_USE_ZONE',
  'CONSERVATION_BUFFER',
] as const;

/** M34 Blue Economy — public Marine Spatial Plan viewer: zone designations + a point-in-polygon lookup tool, MSP_ADMIN maintenance. */
export function MspViewer() {
  const { persona } = useAuth();
  const isMspAdmin = persona ? persona.roles.includes('MSP_ADMIN') : false;
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showLookup, setShowLookup] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '100' });
    if (category) params.set('category', category);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m34/msp/zones?${params.toString()}`;
  }, [category, appliedQ]);
  const listQ = useApiQuery<Page<MspZone>>(path);

  return (
    <div>
      <PageHeader
        title="Marine Spatial Plan Viewer"
        subtitle="Seychelles Marine Spatial Plan 2020-2030 zone designations — the public MSP map's data source (M34)."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setShowLookup(true)}>
              Point lookup
            </button>
            {isMspAdmin ? (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + New zone
              </button>
            ) : null}
          </>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Zone code or name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {MSP_ZONE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>
      {!isMspAdmin ? <RoleNotice roles={['MSP_ADMIN']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No MSP zones match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Category</th>
                <th className="table-head">District</th>
                <th className="table-head">Active</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((z) => (
                <tr key={z.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedCode(z.code)}>
                  <td className="table-cell font-mono text-xs font-medium">{z.code}</td>
                  <td className="table-cell">{z.name}</td>
                  <td className="table-cell text-xs">{z.category.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{z.district ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={z.active ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedCode ? (
        <ZoneDetailModal
          code={selectedCode}
          isMspAdmin={isMspAdmin}
          onClose={() => setSelectedCode(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <NewZoneModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {showLookup ? <LookupModal onClose={() => setShowLookup(false)} /> : null}
    </div>
  );
}

function ZoneDetailModal({ code, isMspAdmin, onClose, onChanged }: { code: string; isMspAdmin: boolean; onClose: () => void; onChanged: () => void }) {
  const zq = useApiQuery<MspZone>(`/api/v1/m34/msp/zones/${code}`);
  const z = zq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retire = async () => {
    if (!z) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m34/msp/zones/${z.id}/retire`);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={z ? `${z.code} — ${z.name}` : 'MSP zone'} onClose={onClose} wide>
      {zq.error ? <ErrorNote message={zq.error} /> : null}
      {zq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {z ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Category" value={z.category.replaceAll('_', ' ')} />
            <Detail label="District" value={z.district ?? '—'} />
            <Detail label="Plan" value={z.planName} />
            <Detail label="Effective from" value={formatDate(z.effectiveFrom)} />
            <Detail label="Active" value={<StatusBadge status={z.active ? 'ACTIVE' : 'CLOSED'} />} />
          </div>
          {z.description ? <Detail label="Description" value={z.description} /> : null}
          <div>
            <div className="field-label">Geometry (GeoJSON)</div>
            <pre className="max-h-40 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-emerald-300">{JSON.stringify(z.geometry, null, 2)}</pre>
          </div>
          {isMspAdmin && z.active ? (
            <div className="border-t border-slate-100 pt-4">
              <button className="btn-danger" disabled={busy} onClick={retire}>
                {busy ? 'Retiring…' : 'Retire zone'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewZoneModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<(typeof MSP_ZONE_CATEGORIES)[number]>('MARINE_PROTECTED_AREA');
  const [district, setDistrict] = useState('');
  const [description, setDescription] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m34/msp/zones', {
        code,
        name,
        category,
        geometry: { type: 'Point', coordinates: [Number.parseFloat(lng), Number.parseFloat(lat)] },
        district: district || undefined,
        description: description || undefined,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New MSP zone designation" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="MPA_STA_ANNE" />
          </Field>
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as (typeof MSP_ZONE_CATEGORIES)[number])}>
              {MSP_ZONE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Representative latitude">
            <input className="field-input" type="number" step="0.000001" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-4.6127" />
          </Field>
          <Field label="Representative longitude">
            <input className="field-input" type="number" step="0.000001" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.6559" />
          </Field>
          <Field label="District (optional)">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
        </div>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <p className="text-xs text-slate-400">
          Point geometry is used for simplicity here; the backend accepts any GeoJSON Point or Polygon.
        </p>
        <button className="btn-primary w-full" disabled={busy || !code || !name || !lat || !lng} onClick={submit}>
          {busy ? 'Creating…' : 'Create zone'}
        </button>
      </div>
    </Modal>
  );
}

function LookupModal({ onClose }: { onClose: () => void }) {
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [result, setResult] = useState<{ lat: number; lng: number; items: MspZone[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.get(`/api/v1/m34/msp/lookup?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="MSP point lookup" onClose={onClose}>
      <p className="mb-3 text-sm text-slate-500">Find which MSP zones contain a given point — the same API the public map surface uses.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Latitude">
          <input className="field-input" type="number" step="0.000001" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-4.6796" />
        </Field>
        <Field label="Longitude">
          <input className="field-input" type="number" step="0.000001" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.4920" />
        </Field>
      </div>
      <button className="btn-primary mt-3 w-full" disabled={busy || !lat || !lng} onClick={lookup}>
        {busy ? 'Looking up…' : 'Look up'}
      </button>
      {error ? (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {result ? (
        result.items.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No MSP zone contains this point.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-sm">
            {result.items.map((z) => (
              <li key={z.id} className="flex items-center justify-between border-b border-slate-100 py-1">
                <span>
                  {z.name} <span className="font-mono text-xs text-slate-400">({z.code})</span>
                </span>
                <StatusBadge status={z.category} />
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Modal>
  );
}
